import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import type { Message } from "../types/chat";

export interface Favorite {
  id: number;
  question_id?: string;  
  user_id: number;
  created_at: string;
  title: string;
  question_text: string;
  sql_correct: string;
  dialect: string;
  tags?: string[] | null;
  is_pinned?: boolean;
  usage_count?: number;
  last_used_at?: string | null;
  updated_at?: string;
}

export interface FavoritePayload {
  title: string;
  question_text: string;
  sql_correct: string;
  dialect: string;
  tags?: string[];
  is_pinned?: boolean;
}

type FavById = Record<string, Favorite>;
type FavByText = Record<string, Favorite>;

const normCache = new Map<string, string>();
const norm = (s?: string): string => {
  if (!s) return "";
  if (normCache.has(s)) return normCache.get(s)!;
  const result = s.trim().toLowerCase();
  if (normCache.size > 1000) normCache.clear(); 
  normCache.set(s, result);
  return result;
};

export const useFavorites = () => {
  const [favoritesById, setFavoritesById] = useState<FavById>({});
  const [favoritesByText, setFavoritesByText] = useState<FavByText>({});
  const pendingRequests = useRef(new Set<string>());

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/favorites/me");
      const data = res.data || [];
      
      const byId: FavById = {};
      const byText: FavByText = {};
      
      for (const fav of data) {
        const qid = (fav as any).question_id;
        if (qid) byId[qid] = fav;
        
        const qt = norm(fav.question_text);
        if (qt) byText[qt] = fav;
      }
      
      setFavoritesById(byId);
      setFavoritesByText(byText);
    } catch (err) {
    }
  }, []);

  const addFavorite = useCallback(async (userMessage: Message, botMessage: Message, sqlQuery?: string) => {
    const requestId = `add_${norm(userMessage.text)}`;
    if (pendingRequests.current.has(requestId)) return;
    
    try {
      pendingRequests.current.add(requestId);
      const title = userMessage.text?.substring(0, 100) || "Favorite question";
      const payload: FavoritePayload = {
        title,
        question_text: userMessage.text || "",
        sql_correct: sqlQuery || "",
        dialect: "mysql",
        tags: [],
        is_pinned: false
      };
      const res = await axiosInstance.post("/favorites/", payload);
      const fav: Favorite = res.data;
      
      setFavoritesByText(prev => {
        const key = norm(userMessage.text);
        if (prev[key] === fav) return prev;
        return { ...prev, [key]: fav };
      });
      return res.data;
    } catch (err) {
      // Error("Failed to add favorite", err);
      throw err;
    } finally {
      pendingRequests.current.delete(requestId);
    }
  }, []);

  const addFavoriteWithPayload = useCallback(async (questionId: string, payload: FavoritePayload) => {
    const requestId = `addPayload_${questionId}`;
    if (pendingRequests.current.has(requestId)) return;
    
    try {
      pendingRequests.current.add(requestId);
      const res = await axiosInstance.post("/favorites/", payload);
      const fav: Favorite = res.data;
      
      setFavoritesById(prev => ({ ...prev, [questionId]: fav }));
      if (payload.question_text) {
        setFavoritesByText(prev => ({ ...prev, [norm(payload.question_text)]: fav }));
      }
      return res.data;
    } catch (err) {
      // Error("Failed to add favorite", err);
      throw err;
    } finally {
      pendingRequests.current.delete(requestId);
    }
  }, []);

  const removeFavoriteByText = useCallback(async (questionText: string) => {
    const key = norm(questionText);
    const requestId = `remove_${key}`;
    
    if (pendingRequests.current.has(requestId)) return;
    
    try {
      pendingRequests.current.add(requestId);
      const fav = favoritesByText[key];
      if (!fav) return;
      
      await axiosInstance.delete(`/favorites/${fav.id}`);
      
      setFavoritesByText(prev => {
        if (!prev[key]) return prev;
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    } catch (err) {
      // Error("Failed to remove favorite", err);
      throw err;
    } finally {
      pendingRequests.current.delete(requestId);
    }
  }, [favoritesByText]);

  const isFavorite = useCallback((messageId: string) => {
    return !!favoritesById[messageId];
  }, [favoritesById]);

  const isFavoriteByText = useCallback((questionText?: string) => {
    return !!favoritesByText[norm(questionText)];
  }, [favoritesByText]);

  useEffect(() => { 
    fetchFavorites(); 
  }, [fetchFavorites]);

  return {
    favorites: favoritesById,
    addFavorite,
    addFavoriteWithPayload,
    removeFavoriteByText,   
    fetchFavorites,
    isFavorite,
    isFavoriteByText,
    favoritesByText   
  };
};
